import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Checkbox,
  Fade,
  Paper,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import { ApprovalCard, ApprovalDetailModal } from '@/components/approvals';
import {
  useApprovals,
  useApprovalStats,
  useApproveApproval,
  useDeclineApproval,
  approvalRefusalKey,
  type ApprovalListItem,
} from '@/hooks/useApprovals';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useNotify } from '@/stores/notificationStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useDebounce } from '@/hooks/useDebounce';
import { neutralGrey } from '@/styles/theme';
import type { Approval, ApprovalStatus, ApprovalType } from '@/types';
import { useUrlMultiSelectFilter } from '@/hooks/useUrlMultiSelectFilter';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';

// The API's approval_type values (#2851): its `type` filter accepts only these.
const typeOptions: { value: ApprovalType | 'all'; labelKey: string }[] = [
  { value: 'all', labelKey: 'allTypes' },
  { value: 'extra_work', labelKey: 'extraWork' },
  { value: 'high_value', labelKey: 'highValue' },
  { value: 'customer_declined', labelKey: 'customerDeclined' },
  { value: 'warranty_claim', labelKey: 'warrantyClaim' },
  { value: 'discount_request', labelKey: 'discountRequest' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: 'warning.main',
  approved: 'success.main',
  rejected: 'error.main',
  expired: neutralGrey, // grey (Dark 5)
};

const STATUS_ICONS: Record<string, React.ElementType> = {
  pending: HourglassEmptyIcon,
  approved: ThumbUpAltIcon,
  rejected: ThumbDownIcon,
  expired: TimerOffIcon,
};

const ALL_STATUSES: ApprovalStatus[] = ['pending', 'approved', 'rejected', 'expired'];

/**
 * Chip → API statuses (#2847). The list is filtered client-side by chip, so
 * the request has to carry every status a selected chip can show; without
 * `status` the API returns only the pending ones.
 */
const API_STATUSES_BY_FILTER: Record<ApprovalStatus, readonly string[]> = {
  pending: ['awaiting_manager', 'awaiting_dual_approval', 'awaiting_customer', 'escalated'],
  approved: ['approved'],
  rejected: ['declined'],
  expired: ['expired'],
};

// Map API approval to frontend Approval type.
// Exported for tests (#2907): a missing stored number is a placeholder, never TM-+id.
export function mapApiApprovalToFrontend(
  item: ApprovalListItem,
  noWorkOrderNumber: string,
): Approval {
  return {
    id: item.id,
    type: (item.approvalType as ApprovalType) ?? 'additional_work',
    status: mapApiStatus(item.status),
    title: item.description ?? '',
    description: item.customerDescription ?? item.description ?? '',
    workOrderId: item.bookingId ?? '',
    workOrderNumber: item.workOrderNumber ?? noWorkOrderNumber,
    vehicleId: '',
    customerId: item.customerId ?? '',
    customer: {
      id: item.customerId ?? '',
      firstName: (item.customerName ?? '').split(' ')[0] ?? '',
      lastName: (item.customerName ?? '').split(' ').slice(1).join(' ') ?? '',
      email: '',
      phone: item.customerPhone ?? '',
    },
    vehicle: {
      id: '',
      customerId: item.customerId ?? '',
      registrationNumber: item.vehicleRegNumber ?? '',
      make: item.vehicleMake ?? '',
      model: item.vehicleModel ?? '',
      year: 0,
    },
    mechanicId: item.mechanicId ?? '',
    mechanic: {
      id: item.mechanicId ?? '',
      firstName: (item.mechanicName ?? '').split(' ')[0] ?? '',
      lastName: (item.mechanicName ?? '').split(' ').slice(1).join(' ') ?? '',
      email: '',
      phone: '',
      skills: [],
      isAvailable: true,
    },
    items: [],
    totalCents: item.totalCostCents,
    photos: [],
    urgency: (item.urgency as Approval['urgency']) ?? 'soon',
    requestedAt: item.createdAt,
    sentVia: 'whatsapp',
    expiresAt: item.expiresAt ?? undefined,
    respondedAt: undefined,
  };
}

function mapApiStatus(status: string): ApprovalStatus {
  const statusMap: Record<string, ApprovalStatus> = {
    awaiting_manager: 'pending',
    awaiting_dual_approval: 'pending',
    awaiting_customer: 'pending',
    escalated: 'pending',
    approved: 'approved',
    declined: 'rejected',
    expired: 'expired',
    auto_approved: 'approved',
  };
  return statusMap[status] ?? 'pending';
}

export default function ApprovalsPage() {
  const { t } = useTranslation('approvals');
  const { t: tc } = useTranslation('common');
  const { t: tWorkorders } = useTranslation('workorders');
  const notify = useNotify();
  const isMobile = useIsMobile();

  /*
    #878 extracted this into useUrlMultiSelectFilter — but nothing ever
    called the hook, so all four pages kept their own verbatim copy of
    ~50 lines (#2726). This is that migration, finally.
  */
  const { selectedFilters, setSelectedFilters } = useUrlMultiSelectFilter({
    allFilters: ALL_STATUSES,
    storageKey: 'vs-filter-approvals',
  });

  const handleToggleFilter = useCallback((filterKey: string) => {
    const isSelected = selectedFilters.includes(filterKey);
    if (isSelected) {
      setSelectedFilters(selectedFilters.filter((s) => s !== filterKey));
    } else {
      setSelectedFilters([...selectedFilters, filterKey]);
    }
    // Reset bulk selection on filter change
    setSelectedIds(new Set());
  }, [selectedFilters, setSelectedFilters]);

  // Search state
  // Search lives in the URL so Back restores it (#4100).
  const [searchQuery, setSearchQuery] = useUrlSearchParam('q');
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Filters
  const [typeFilter, setTypeFilter] = useState<ApprovalType | 'all'>('all');
  const [mechanicFilter, setMechanicFilter] = useState<string | 'all'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Modal state
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Dashboard stats (always active)
  const { data: stats } = useApprovalStats();

  // Main data query — always active, fetch all statuses
  const {
    data: searchData,
    isLoading: searchLoading,
    isError: searchError,
    error: searchErrorObj,
    refetch: refetchSearch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useApprovals({
    status: selectedFilters
      .flatMap((filter) => API_STATUSES_BY_FILTER[filter as ApprovalStatus] ?? [])
      .join(',') || undefined,
    q: debouncedQuery || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    mechanicId: mechanicFilter !== 'all' ? mechanicFilter : undefined,
  });

  const { data: mechanics = [] } = useMechanics();
  const approveApproval = useApproveApproval();
  const declineApproval = useDeclineApproval();

  // Flatten search results and map to frontend type
  const allResults = useMemo(() => {
    if (!searchData?.pages) return [];
    return searchData.pages.flatMap((p) => p.data);
  }, [searchData]);

  const allApprovals: Approval[] = useMemo(
    () =>
      allResults.map((item) => {
        const mapped = mapApiApprovalToFrontend(item, tWorkorders('noWorkOrderNumber'));
        if (!mapped.title) mapped.title = t('approvalRequest');
        return mapped;
      }),
    [allResults, t, tWorkorders],
  );

  // Client-side multi-select filter
  const allSelected = selectedFilters.length === ALL_STATUSES.length && ALL_STATUSES.every((s) => selectedFilters.includes(s));

  const filteredApprovals = useMemo(() => {
    if (allSelected) return allApprovals;
    return allApprovals.filter((a) => selectedFilters.includes(a.status));
  }, [allApprovals, selectedFilters, allSelected]);

  // Chip counts are totals from the stats endpoint (#2853), independent of
  // which chips are on and of how many pages are loaded. Until the stats
  // arrive the chips show a placeholder: counting the fetched rows meanwhile
  // flashed unrelated numbers (#2857).
  const statusCountMap = useMemo<Record<string, number> | null>(
    () => (stats?.byStatus ? { ...stats.byStatus } : null),
    [stats],
  );

  // Bulk selection is only enabled when pending filter is active
  const bulkSelectEnabled = selectedFilters.includes('pending');

  // Get pending approvals in filtered list for bulk selection
  const selectablePendingApprovals = useMemo(
    () => filteredApprovals.filter((a) => a.status === 'pending'),
    [filteredApprovals],
  );

  // Selection handlers
  const handleSelect = useCallback((approvalId: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(approvalId);
      } else {
        next.delete(approvalId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = selectablePendingApprovals.map((a) => a.id);
    setSelectedIds(new Set(allIds));
  }, [selectablePendingApprovals]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Bulk action handlers
  const handleBulkApprove = useCallback(() => {
    const count = selectedIds.size;
    const ids = Array.from(selectedIds);
    ids.forEach((approvalId) => {
      approveApproval.mutate({ id: approvalId });
    });
    setSelectedIds(new Set());
    notify.success(
      t('bulkApproveSuccess', { count }),
      t('bulkApproveDescription', { count }),
    );
  }, [selectedIds, approveApproval, notify, t]);

  const handleBulkReject = useCallback(() => {
    const count = selectedIds.size;
    const ids = Array.from(selectedIds);
    ids.forEach((approvalId) => {
      declineApproval.mutate({ id: approvalId });
    });
    setSelectedIds(new Set());
    notify.success(
      t('bulkRejectSuccess', { count }),
      t('bulkRejectDescription', { count }),
    );
  }, [selectedIds, declineApproval, notify, t]);

  const handleViewApproval = (approval: Approval) => {
    setSelectedApproval(approval);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedApproval(null);
  };

  /*
    "Lähetä muistutus" is gone until there is something to send (#2712).

    The handler raised `notify.success(t('reminderSent'))` and then only
    `console.log`ged — the customer got no reminder while the supervisor was
    told one had been sent, so nobody followed up. staff-api has no reminder
    endpoint for approvals (approvals.ts exposes approve / decline / escalate
    only), though `approvalRequests.reminderCount` is already in the schema
    for when one is added.

    Both ApprovalCard and ApprovalDetailModal render the button only when an
    `onResend` prop is passed, so leaving it off hides it in both places
    rather than leaving a control that lies.
  */

  const handleApprove = (approvalId: string) => {
    approveApproval.mutate(
      { id: approvalId },
      {
        onSuccess: (data) => {
          // dual_manager: the first approval leaves the request open (#3026).
          if (data.approval.awaitingSecondApproval === true) {
            notify.info(t('firstApprovalRecorded'), t('firstApprovalRecordedDescription'));
          } else {
            notify.success(t('approvalMarked'), t('approvalMarkedDescription'));
          }
          handleCloseModal();
        },
        onError: (error) => notify.error(t('error'), t(approvalRefusalKey(error) ?? 'approveSaveFailed')),
      },
    );
  };

  const handleReject = (approvalId: string) => {
    declineApproval.mutate(
      { id: approvalId },
      {
        onSuccess: () => {
          notify.success(t('approvalMarked'), t('approvalMarkedDescription'));
          handleCloseModal();
        },
        onError: (error) => notify.error(t('error'), t(approvalRefusalKey(error) ?? 'rejectSaveFailed')),
      },
    );
  };

  return (
    <Box>
      {/* Header: title */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
      </Box>

      {/* Search bar */}
      <TextField
        size="small"
        placeholder={t('searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('clearSearch')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Multi-select status chip toggles + filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {ALL_STATUSES.map((statusKey) => {
          const isSelected = selectedFilters.includes(statusKey);
          const count = statusCountMap ? (statusCountMap[statusKey] ?? 0) : '–';
          const color = STATUS_COLORS[statusKey] ?? neutralGrey;
          const IconComp = STATUS_ICONS[statusKey] ?? HourglassEmptyIcon;

          return (
            <Tooltip key={statusKey} title={t(statusKey)}>
              <Chip
                icon={<IconComp sx={{ fontSize: 16 }} />}
                label={count}
                size="small"
                onClick={() => handleToggleFilter(statusKey)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  minWidth: 52,
                  ...(isSelected
                    ? {
                        bgcolor: color,
                        color: 'common.white',
                        '& .MuiChip-icon': { color: 'common.white' },
                        '&:hover': { bgcolor: color, opacity: 0.85 },
                      }
                    : {
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '& .MuiChip-icon': { color },
                        '&:hover': { bgcolor: 'action.hover' },
                      }),
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Additional filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '0 0 auto' }, minWidth: { md: 150 } }}>
          <InputLabel>{t('type')}</InputLabel>
          <Select
            value={typeFilter}
            label={t('type')}
            onChange={(e: SelectChangeEvent) => setTypeFilter(e.target.value as ApprovalType | 'all')}
          >
            {typeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '0 0 auto' }, minWidth: { md: 150 } }}>
          <InputLabel>{t('mechanic')}</InputLabel>
          <Select
            value={mechanicFilter}
            label={t('mechanic')}
            onChange={(e: SelectChangeEvent) => setMechanicFilter(e.target.value)}
          >
            <MenuItem value="all">{t('allMechanics')}</MenuItem>
            {mechanics.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isMobile && (
          <>
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, value) => value && setViewMode(value)}
              size="small"
            >
              <ToggleButton value="list">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="grid">
                <GridViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </Box>

      {/* Bulk Actions Toolbar */}
      <Fade in={selectedIds.size > 0}>
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            p: 1.5,
            display: selectedIds.size > 0 ? 'flex' : 'none',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={selectedIds.size === selectablePendingApprovals.length && selectablePendingApprovals.length > 0}
              indeterminate={selectedIds.size > 0 && selectedIds.size < selectablePendingApprovals.length}
              onChange={(e) => {
                if (e.target.checked) {
                  handleSelectAll();
                } else {
                  handleClearSelection();
                }
              }}
              sx={{
                color: 'primary.contrastText',
                '&.Mui-checked': { color: 'primary.contrastText' },
                '&.MuiCheckbox-indeterminate': { color: 'primary.contrastText' },
              }}
            />
            <Typography variant="body2" fontWeight={500} noWrap>
              {t('selectedCount', { count: selectedIds.size })}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleBulkApprove}
              sx={{
                backgroundColor: 'success.main',
                '&:hover': { backgroundColor: 'success.dark' },
              }}
            >
              {t('approveSelected')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<CancelOutlinedIcon />}
              onClick={handleBulkReject}
              sx={{
                backgroundColor: 'error.main',
                '&:hover': { backgroundColor: 'error.dark' },
              }}
            >
              {t('rejectSelected')}
            </Button>
            <Button
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleClearSelection}
              sx={{ color: 'primary.contrastText' }}
            >
              {t('clearSelection')}
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Results */}
      {searchError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void refetchSearch()}
            >
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('errorLoading')}:{' '}
          {searchErrorObj instanceof Error
            ? searchErrorObj.message
            : tc('unknownError')}
        </Alert>
      ) : (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {searchLoading
              ? tc('loading')
              : t('searchResultCount', { count: filteredApprovals.length })}
            {!searchLoading && selectedFilters.length === 0 && (
              <Typography variant="body2" color="warning.main" sx={{ ml: 1 }} component="span">
                — {tc('selectStatus')}
              </Typography>
            )}
          </Typography>

          {filteredApprovals.length === 0 && !searchLoading ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography color="text.secondary">{t('noApprovals')}</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: viewMode === 'grid' ? '1fr 1fr' : '1fr',
                  lg: viewMode === 'grid' ? '1fr 1fr 1fr' : '1fr',
                },
                gap: 2,
              }}
            >
              {filteredApprovals.map((approval) => (
                <ApprovalCard
                  key={approval.id}
                  approval={approval}
                  onView={handleViewApproval}
                  selectable={bulkSelectEnabled && approval.status === 'pending'}
                  isSelected={selectedIds.has(approval.id)}
                  onSelect={handleSelect}
                />
              ))}
            </Box>
          )}

          {hasNextPage && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
            >
              <Button
                variant="outlined"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? tc('loading') : t('loadMore')}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Detail Modal */}
      <ApprovalDetailModal
        approval={selectedApproval}
        open={modalOpen}
        onClose={handleCloseModal}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </Box>
  );
}
